import { Injectable, NgZone } from '@angular/core';
import { getApp, getApps, initializeApp, FirebaseApp } from 'firebase/app';
import {
  getAuth,
  Auth,
  signInWithEmailAndPassword,
  signOut,
  onIdTokenChanged,
  sendPasswordResetEmail,
  confirmPasswordReset,
  setPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  User,
  EmailAuthProvider,
  reauthenticateWithCredential,
} from 'firebase/auth';
import { Observable, BehaviorSubject, Subscription, from, timer, throwError } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { environment } from 'src/environments/environment';


@Injectable({ providedIn: 'root' })
export class FirebaseAuthService {
  private app: FirebaseApp;
  private auth: Auth;
  private tokenSubject = new BehaviorSubject<string | null>(null);
  private authReady = new BehaviorSubject<boolean>(false);
  private refreshTimerSub: Subscription | null = null;

  token$ = this.tokenSubject.asObservable();
  authReady$ = this.authReady.asObservable();

  constructor(private ngZone: NgZone) {
    // SHFT-5757: the internal build's pre-bootstrap gate (src/main.ts) and/or
    // RemoteConfigService may have already initialized the default Firebase
    // app before this service is constructed - reuse it instead of throwing
    // on a duplicate initializeApp() call.
    this.app = getApps().length ? getApp() : initializeApp(environment.firebaseConfig);
    this.auth = getAuth(this.app);

    if (environment.firebaseTenantId) {
      this.auth.tenantId = environment.firebaseTenantId;
    }

    onIdTokenChanged(this.auth, (user: User | null) => {
      this.ngZone.run(async () => {
        if (user) {
          const token = await user.getIdToken();
          this.tokenSubject.next(token);
          this.scheduleTokenRefresh(token);
        } else {
          this.tokenSubject.next(null);
          this.clearRefreshTimer();
        }
        if (!this.authReady.value) {
          this.authReady.next(true);
        }
      });
    });
  }

  private scheduleTokenRefresh(token: string): void {
    this.clearRefreshTimer();
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const expiresAt = payload.exp * 1000;
      const delay = Math.max(expiresAt - Date.now() - 5 * 60 * 1000, 0);
      this.refreshTimerSub = timer(delay).pipe(
        switchMap(() => from(this.getIdToken(true)))
      ).subscribe();
    } catch {
      // If token can't be decoded, skip scheduling
    }
  }

  private clearRefreshTimer(): void {
    if (this.refreshTimerSub) {
      this.refreshTimerSub.unsubscribe();
      this.refreshTimerSub = null;
    }
  }

  signIn(email: string, password: string, remember = true): Observable<string> {
    return from(
      (async () => {
        await setPersistence(this.auth, remember ? browserLocalPersistence : browserSessionPersistence);
        const cred = await signInWithEmailAndPassword(this.auth, email, password);
        return cred.user.getIdToken();
      })()
    );
  }

  signOut(): Observable<void> {
    return from(signOut(this.auth));
  }

  getIdToken(forceRefresh = false): Promise<string | null> {
    const user = this.auth.currentUser;
    if (!user) return Promise.resolve(null);
    return user.getIdToken(forceRefresh);
  }

  sendPasswordResetEmail(email: string): Observable<void> {
    return from(sendPasswordResetEmail(this.auth, email));
  }

  confirmPasswordReset(oobCode: string, newPassword: string): Observable<void> {
    return from(confirmPasswordReset(this.auth, oobCode, newPassword));
  }

  get currentUser(): User | null {
    return this.auth.currentUser;
  }

  reauthenticate(password: string): Observable<void> {
    return from(
      (async () => {
        const user = this.auth.currentUser;
        if (!user || !user.email) {
          throw new Error('No user is currently logged in');
        }
        const credential = EmailAuthProvider.credential(user.email, password);
        await reauthenticateWithCredential(user, credential);
      })()
    );
  }
}
